import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface AdminNotification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type: string;
  link: string | null;
  is_read: boolean;
  metadata: any;
  created_at: string;
  snoozed_until?: string | null;
  archived_at?: string | null;
  resolved_at?: string | null;
  assigned_task_id?: string | null;
}

export function useAdminNotifications() {
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // This is the Nomad Fest app, so the inbox is ALWAYS Nomad Fest scope —
    // legacy null-tagged rows OR explicit 'nomad-fest'. We deliberately do NOT
    // derive scope from admin_users.app: cross-project admins (e.g. also tagged
    // 'frihuborg') would otherwise see other projects' notifications leaking in.
    const query = (supabase.from('admin_notifications' as any) as any)
      .select('*')
      .eq('user_id', user.id)
      .or('app.is.null,app.eq.nomad-fest')
      .order('created_at', { ascending: false })
      .limit(50);

    const { data, error } = await query;

    if (!error && data) {
      const typed = data as unknown as AdminNotification[];
      setNotifications(typed);
      setUnreadCount(typed.filter(n => !n.is_read).length);
    }
    setLoading(false);
  }, []);

  const markAsRead = useCallback(async (notificationId: string) => {
    await supabase
      .from('admin_notifications' as any)
      .update({ is_read: true } as any)
      .eq('id', notificationId);

    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const deleteNotification = useCallback(async (notificationId: string) => {
    const { error } = await supabase
      .from('admin_notifications' as any)
      .delete()
      .eq('id', notificationId);
    if (error) return false;
    setNotifications(prev => {
      const removed = prev.find(n => n.id === notificationId);
      if (removed && !removed.is_read) setUnreadCount(c => Math.max(0, c - 1));
      return prev.filter(n => n.id !== notificationId);
    });
    return true;
  }, []);

  // Patch one notification in the DB (own row via RLS) and in local state.
  const patch = useCallback(async (id: string, updates: Record<string, any>) => {
    const { error } = await supabase
      .from('admin_notifications' as any)
      .update(updates as any)
      .eq('id', id);
    if (error) return false;
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, ...updates } as AdminNotification : n));
    return true;
  }, []);

  const snooze = useCallback((id: string, until: Date) =>
    patch(id, { snoozed_until: until.toISOString(), is_read: true }), [patch]);
  const unsnooze = useCallback((id: string) => patch(id, { snoozed_until: null }), [patch]);
  const archive = useCallback((id: string) => patch(id, { archived_at: new Date().toISOString(), is_read: true }), [patch]);
  const unarchive = useCallback((id: string) => patch(id, { archived_at: null }), [patch]);
  const resolve = useCallback((id: string) => patch(id, { resolved_at: new Date().toISOString(), is_read: true }), [patch]);
  const reopen = useCallback((id: string) => patch(id, { resolved_at: null }), [patch]);
  const setAssignedTask = useCallback((id: string, taskId: string) =>
    patch(id, { assigned_task_id: taskId, resolved_at: new Date().toISOString() }), [patch]);

  const markAllAsRead = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await supabase
      .from('admin_notifications' as any)
      .update({ is_read: true } as any)
      .eq('user_id', user.id)
      .eq('is_read', false);

    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  }, []);

  useEffect(() => {
    fetchNotifications();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('admin-notifications')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'admin_notifications',
      }, (payload) => {
        const newNotification = payload.new as unknown as AdminNotification;
        // Only for the current user AND within Nomad Fest scope (ignore other
        // projects' notifications so they don't leak into this inbox).
        const app = (newNotification as any).app;
        const inScope = app == null || app === 'nomad-fest';
        supabase.auth.getUser().then(({ data: { user } }) => {
          if (user && newNotification.user_id === user.id && inScope) {
            setNotifications(prev => [newNotification, ...prev]);
            setUnreadCount(prev => prev + 1);
          }
        });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchNotifications]);

  return {
    notifications, unreadCount, loading,
    markAsRead, markAllAsRead, deleteNotification,
    snooze, unsnooze, archive, unarchive, resolve, reopen, setAssignedTask,
    refetch: fetchNotifications,
  };
}
